import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { personalInfo, stats, projects } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { ArrowRight, Code, Palette, GraduationCap, Award } from 'lucide-react';

const HomePage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  // Get featured projects (2 from each category)
  const featuredProjects = [
    ...projects.fintech.slice(0, 2),
    ...projects.gaming.slice(0, 1),
    ...projects.cinematography.slice(0, 1),
  ];

  return (
    <div style={{ backgroundColor: COLORS.background }}>
      {/* Hero Section */}
      <section
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          paddingTop: '80px',
        }}
      >
        {/* Animated background */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at 20% 50%, ${COLORS.primary}10 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${COLORS.accent}10 0%, transparent 50%)`,
          }}
        />

        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: `${SPACING.xxxl} ${SPACING.lg}`,
            width: '100%',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1fr',
              gap: SPACING.xxl,
              alignItems: 'center',
            }}
            className="hero-grid"
          >
            {/* Left Content */}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 800ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {/* Status Badge */}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: SPACING.xs,
                  padding: `${SPACING.xs} ${SPACING.md}`,
                  backgroundColor: `${COLORS.primary}15`,
                  border: `1px solid ${COLORS.primary}40`,
                  borderRadius: '50px',
                  marginBottom: SPACING.lg,
                }}
              >
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: COLORS.primary,
                    animation: 'pulse 2s ease-in-out infinite',
                  }}
                />
                <span
                  style={{
                    fontSize: '14px',
                    color: COLORS.primary,
                    fontWeight: '500',
                  }}
                >
                  {personalInfo.status}
                </span>
              </div>

              <h1
                style={{
                  fontSize: '72px',
                  lineHeight: '1.1',
                  fontWeight: '700',
                  color: COLORS.text.primary,
                  marginBottom: SPACING.xs,
                }}
              >
                {personalInfo.name}
              </h1>
              
              <p
                style={{
                  fontSize: '18px',
                  lineHeight: '1.4',
                  fontWeight: '500',
                  color: COLORS.text.muted,
                  marginBottom: SPACING.md,
                }}
              >
                Also known as <span style={{ color: COLORS.primary, fontWeight: '600' }}>{personalInfo.nickname}</span>
              </p>

              <h2
                style={{
                  fontSize: '24px',
                  lineHeight: '1.4',
                  fontWeight: '600',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING.xs,
                }}
              >
                {personalInfo.title}
              </h2>
              
              <p
                style={{
                  fontSize: '18px',
                  lineHeight: '1.4',
                  fontWeight: '500',
                  color: COLORS.primary,
                  marginBottom: SPACING.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING.xs,
                }}
              >
                {personalInfo.subtitle} • <a 
                  href={personalInfo.companyWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: COLORS.primary,
                    textDecoration: 'none',
                    borderBottom: `1px solid ${COLORS.primary}`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderBottomWidth = '2px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderBottomWidth = '1px';
                  }}
                >
                  raindas.co
                </a>
              </p>

              <p
                style={{
                  fontSize: '18px',
                  lineHeight: '1.7',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING.xl,
                  maxWidth: '600px',
                }}
              >
                {personalInfo.tagline}
              </p>

              {/* CTAs */}
              <div
                style={{
                  display: 'flex',
                  gap: SPACING.md,
                  flexWrap: 'wrap',
                }}
              >
                <Link to="/projects" style={{ textDecoration: 'none' }}>
                  <Button
                    style={{
                      backgroundColor: COLORS.primary,
                      color: COLORS.background,
                      padding: `${SPACING.md} ${SPACING.xl}`,
                      fontSize: '16px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING.xs,
                      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: `0 0 40px ${COLORS.primary}40`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primaryDark;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 8px 50px ${COLORS.primary}60`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 0 40px ${COLORS.primary}40`;
                    }}
                  >
                    View My Work <ArrowRight size={18} />
                  </Button>
                </Link>

                <Link to="/contact" style={{ textDecoration: 'none' }}>
                  <Button
                    variant="outline"
                    style={{
                      backgroundColor: 'transparent',
                      color: COLORS.text.primary,
                      padding: `${SPACING.md} ${SPACING.xl}`,
                      fontSize: '16px',
                      fontWeight: '600',
                      border: `2px solid ${COLORS.border}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.cardSurface;
                      e.currentTarget.style.borderColor = COLORS.primary;
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = COLORS.border;
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    Let's Connect
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Portrait */}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 800ms cubic-bezier(0.4, 0, 0.2, 1) 100ms',
                position: 'relative',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: `0 20px 60px ${COLORS.primary}30`,
                }}
              >
                <img
                  src={personalInfo.portraitImage}
                  alt={personalInfo.name}
                  style={{
                    width: '100%',
                    height: '600px',
                    objectFit: 'cover',
                    objectPosition: 'center -50px',
                    display: 'block',
                  }}
                />
                {/* Overlay */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(135deg, ${COLORS.primary}15 0%, transparent 50%, ${COLORS.background}40 100%)`,
                  }}
                />
                {/* Glass card */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: SPACING.lg,
                    left: SPACING.lg,
                    right: SPACING.lg,
                    padding: SPACING.lg,
                    background: 'rgba(0, 0, 0, 0.7)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${COLORS.primary}40`,
                    borderRadius: '16px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <p style={{ fontSize: '14px', color: COLORS.text.secondary, marginBottom: '4px' }}>Based in</p>
                      <p style={{ fontSize: '18px', color: COLORS.text.primary, fontWeight: '600' }}>
                        {personalInfo.location}
                      </p>
                    </div>
                    <div
                      style={{
                        padding: `${SPACING.sm} ${SPACING.md}`,
                        backgroundColor: COLORS.primary,
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: COLORS.background,
                      }}
                    >
                      {stats.experience} Years
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.5; transform: scale(0.8); }
          }
          @media (max-width: 1024px) {
            .hero-grid {
              grid-template-columns: 1fr !important;
              gap: 48px !important;
            }
          }
        `}</style>
      </section>

      {/* Stats Section */}
      <section
        style={{
          padding: `${SPACING.xxl} ${SPACING.lg}`,
          backgroundColor: COLORS.surface,
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: SPACING.lg,
            }}
          >
            {[
              { value: stats.experience, label: 'Years Experience', icon: Code },
              { value: stats.projects, label: 'Projects Completed', icon: Palette },
              { value: stats.students, label: 'Students Taught', icon: GraduationCap },
              { value: stats.communities, label: 'Communities Led', icon: Award },
            ].map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  style={{
                    padding: SPACING.lg,
                    backgroundColor: COLORS.cardSurface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '16px',
                    textAlign: 'center',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.borderColor = COLORS.primary;
                    e.currentTarget.style.boxShadow = `0 10px 30px ${COLORS.primary}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = COLORS.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <Icon size={32} color={COLORS.primary} style={{ marginBottom: SPACING.sm }} />
                  <div
                    style={{
                      fontSize: '40px',
                      fontWeight: '700',
                      color: COLORS.primary,
                      marginBottom: '4px',
                    }}
                  >
                    {stat.value}
                  </div>
                  <div style={{ fontSize: '14px', color: COLORS.text.secondary }}>{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Preview */}
      <section
        style={{
          padding: `${SPACING.xxxl} ${SPACING.lg}`,
          backgroundColor: COLORS.background,
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: SPACING.xxl }}>
            <div
              style={{
                display: 'inline-block',
                padding: `${SPACING.xs} ${SPACING.md}`,
                backgroundColor: `${COLORS.primary}15`,
                border: `1px solid ${COLORS.primary}40`,
                borderRadius: '50px',
                marginBottom: SPACING.md,
              }}
            >
              <span style={{ fontSize: '14px', color: COLORS.primary, fontWeight: '500' }}>Featured Work</span>
            </div>
            <h2
              style={{
                fontSize: '48px',
                fontWeight: '700',
                color: COLORS.text.primary,
                marginBottom: SPACING.md,
              }}
            >
              Project Portfolio
            </h2>
            <p
              style={{
                fontSize: '18px',
                color: COLORS.text.secondary,
                maxWidth: '600px',
                margin: '0 auto',
              }}
            >
              A showcase of fintech solutions, creative projects, and technical explorations
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: SPACING.lg,
              marginBottom: SPACING.xl,
            }}
          >
            {featuredProjects.map((project) => (
              <div
                key={project.id}
                style={{
                  backgroundColor: COLORS.cardSurface,
                  borderRadius: '20px',
                  border: `1px solid ${COLORS.border}`,
                  overflow: 'hidden',
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-8px)';
                  e.currentTarget.style.borderColor = COLORS.primary;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = COLORS.border;
                }}
              >
                <div style={{ height: '220px', position: 'relative', overflow: 'hidden' }}>
                  <img
                    src={project.image}
                    alt={project.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: SPACING.md,
                      right: SPACING.md,
                      padding: '6px 12px',
                      backgroundColor: `${COLORS.primary}90`,
                      backdropFilter: 'blur(10px)',
                      color: COLORS.background,
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                    }}
                  >
                    {project.category}
                  </div>
                </div>
                <div style={{ padding: SPACING.lg }}>
                  <h3
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: COLORS.text.primary,
                      marginBottom: SPACING.sm,
                    }}
                  >
                    {project.title}
                  </h3>
                  <p
                    style={{
                      fontSize: '14px',
                      color: COLORS.text.secondary,
                      lineHeight: '1.6',
                    }}
                  >
                    {project.description.substring(0, 120)}...
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: 'center' }}>
            <Link to="/projects" style={{ textDecoration: 'none' }}>
              <Button
                style={{
                  backgroundColor: 'transparent',
                  color: COLORS.primary,
                  padding: `${SPACING.md} ${SPACING.xl}`,
                  fontSize: '16px',
                  fontWeight: '600',
                  border: `2px solid ${COLORS.primary}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: SPACING.xs,
                  transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = COLORS.primary;
                  e.currentTarget.style.color = COLORS.background;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = COLORS.primary;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                View All Projects <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
